import { motion } from "framer-motion";
import { useInView } from "framer-motion";
import { useRef } from "react";
import { Check, Sparkles, ArrowRight } from "lucide-react";
import ParticlesBackground from "../ParticlesBackground";

const PricingSection = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });

  const features = [
    "24 Modules complets en e-learning",
    "Ebooks et protocoles exclusifs",
    "48H+ de cours vidéo haute qualité",
    "Groupe privé de suivi avec les anciens élèves",
    "Masterclass exclusives avec Gilles Delieuze",
    "Outils vibratoires et techniques avancées",
    "Accès illimité à la formation",
    "Certificat de formation",
  ];

  return (
    <section
      ref={ref}
      data-testid="pricing-section"
      className="relative py-24 lg:py-32 overflow-hidden"
      style={{ background: "linear-gradient(180deg, #030014 0%, #090920 100%)" }}
    >
      {/* Particles */}
      <ParticlesBackground variant="pricing" />

      {/* Background Glow */}
      <div className="absolute inset-0">
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] bg-purple-500/10 rounded-full blur-3xl" />
      </div>

      <div className="relative z-10 max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8 }}
          className="text-center mb-12"
        >
          <span className="text-cyan-400 text-sm uppercase tracking-[0.2em] mb-4 block">
            Investissement
          </span>
          <h2
            className="text-4xl sm:text-5xl lg:text-6xl font-light text-white mb-4"
            style={{ fontFamily: "'Cormorant Garamond', serif" }}
          >
            <span className="text-gradient-cosmic">Quantic Revolution</span>
          </h2>
          <p className="text-xl text-slate-400">
            Le programme qui révolutionne la thérapie
          </p>
        </motion.div>

        {/* Pricing Card */}
        <motion.div
          initial={{ opacity: 0, y: 40, scale: 0.95 }}
          animate={isInView ? { opacity: 1, y: 0, scale: 1 } : {}}
          transition={{ duration: 0.8, delay: 0.2 }}
          className="pricing-card p-8 sm:p-12"
        >
          <div className="text-center mb-10">
            {/* Price Tag */}
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-amber-500/10 border border-amber-500/30 mb-6">
              <Sparkles className="w-4 h-4 text-amber-400" />
              <span className="text-amber-400 text-sm font-medium">Paiement en plusieurs fois possible</span>
            </div>

            {/* Price */}
            <div className="mb-4">
              <span className="text-slate-400 text-xl">€</span>
              <span
                className="text-6xl sm:text-7xl lg:text-8xl font-light text-gradient-gold"
                style={{ fontFamily: "'Cormorant Garamond', serif" }}
              >
                2555
              </span>
              <span className="text-slate-400 text-xl ml-2">TTC</span>
            </div>

            <p className="text-slate-400">Formation complète • Accès illimité</p>
          </div>

          {/* Features List */}
          <div className="grid sm:grid-cols-2 gap-4 mb-10">
            {features.map((feature, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, x: -20 }}
                animate={isInView ? { opacity: 1, x: 0 } : {}}
                transition={{ duration: 0.4, delay: 0.4 + index * 0.05 }}
                className="flex items-center gap-3"
              >
                <div className="w-5 h-5 rounded-full bg-gradient-to-r from-purple-500 to-cyan-500 flex items-center justify-center flex-shrink-0">
                  <Check className="w-3 h-3 text-white" />
                </div>
                <span className="text-slate-300">{feature}</span>
              </motion.div>
            ))}
          </div>

          {/* CTA Button */}
          <div className="text-center">
            <a
              href="https://gillesdelieuze.com/checkout-seminaire-abondance-2/"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="pricing-cta-button"
              className="btn-primary inline-flex items-center gap-2 text-lg px-10 py-4"
            >
              Je Participe à la Révolution
              <ArrowRight className="w-5 h-5" />
            </a>
            <p className="text-slate-500 text-sm mt-4">
              Garantie satisfait ou remboursé sous 14 jours
            </p>
          </div>
        </motion.div>

        {/* Bottom Message */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6, delay: 0.6 }}
          className="text-center mt-12"
        >
          <p className="text-lg text-slate-300 max-w-2xl mx-auto leading-relaxed">
            Rejoignez-nous dès maintenant et commencez votre transformation avec 
            <span className="text-gradient-cosmic font-medium"> Quantic Revolution</span>
          </p>
        </motion.div>
      </div>
    </section>
  );
};

export default PricingSection;
